/**
 * CORE-BI — the ONE way a browser surface writes `shop_profile.shop_culture`.
 *
 * Every settings card used to read the whole object, patch it in the browser
 * and write the whole object back. `shopCulturePatch.ts` stopped one card from
 * erasing another card's keys, but its own header admitted the remaining race:
 * two managers saving different cards in the same moment, and the later write
 * silently discarded the earlier one (HARD-004).
 *
 * The merge now happens in the database (`patch_shop_culture`, migration
 * 20270206020700) inside ONE UPDATE, so concurrent patches of different keys
 * both survive. The RPC is SECURITY INVOKER: the existing manager/owner UPDATE
 * policy still decides who may write, and the validation + history triggers
 * still run.
 *
 * Patch vocabulary is unchanged from `patchShopCulture`: a key mapped to
 * `undefined` is REMOVED; any other value (including `null`) is SET.
 */

import type { ShopCulturePatch } from './shopCulturePatch';

export interface SplitShopCulturePatch {
  set: Record<string, unknown>;
  remove: string[];
}

/** Separate "set these keys" from "remove these keys" for the server patch. */
export function splitShopCulturePatch(patch: ShopCulturePatch | null | undefined): SplitShopCulturePatch {
  const set: Record<string, unknown> = {};
  const remove: string[] = [];
  if (patch && typeof patch === 'object' && !Array.isArray(patch)) {
    for (const key of Object.keys(patch)) {
      if (patch[key] === undefined) remove.push(key);
      else set[key] = patch[key];
    }
  }
  return { set, remove };
}

/** The minimal client surface this needs; satisfied by the app's Supabase client. */
export interface ShopCultureRpcClient {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: unknown }>;
}

/**
 * Apply a patch atomically on the server and return the stored object.
 * Throws the PostgREST error on refusal (out-of-range value 23514, not a
 * manager/owner 42501) so the caller's toast reports a failed save.
 */
export async function persistShopCulturePatch(
  client: ShopCultureRpcClient,
  shopId: string,
  patch: ShopCulturePatch,
): Promise<Record<string, unknown>> {
  const { set, remove } = splitShopCulturePatch(patch);
  const { data, error } = await client.rpc('patch_shop_culture', {
    p_shop_id: shopId,
    p_set: set,
    p_remove: remove,
  });
  if (error) throw error;
  return (data ?? {}) as Record<string, unknown>;
}
